export function formatPercent(value) {
  if (typeof value !== 'number') {
    return 'n/a';
  }
  return `${Math.round(value * 100)}%`;
}

export function formatMetricValue(value, unit) {
  if (typeof value !== 'number') {
    return 'n/a';
  }
  return `${value} ${unit || ''}`.trim();
}

export function impactSummary(impactEstimate) {
  if (!impactEstimate) {
    return null;
  }
  const lower = impactEstimate.lower_bound;
  const upper = impactEstimate.upper_bound ?? lower;
  if (lower === undefined && upper === undefined) {
    return null;
  }
  if (upper === lower) {
    return `${upper} ${impactEstimate.unit}`;
  }
  return `${lower} to ${upper} ${impactEstimate.unit}`;
}

export function findingStatusLabel(status) {
  switch (status) {
    case 'in_progress':
      return 'In progress';
    case 'resolved':
      return 'Resolved';
    case 'open':
    default:
      return 'Open';
  }
}

export function prevalenceLabel(prevalence) {
  switch (prevalence) {
    case 'isolated':
      return 'Isolated';
    case 'recurring':
      return 'Recurring';
    case 'widespread':
      return 'Widespread';
    default:
      return 'Unknown';
  }
}

export function componentLabel(component) {
  if (!component) {
    return null;
  }
  return component.display_name || component.name;
}

function pluralizeCount(value, noun) {
  return `${value} ${noun}${value === 1 ? '' : 's'}`;
}

function derivedSupportingRunCount(finding) {
  const runCount = typeof finding?.run_count === 'number' && finding.run_count > 0 ? finding.run_count : null;
  const occurrenceRate = typeof finding?.occurrence_rate === 'number' && Number.isFinite(finding.occurrence_rate)
    ? finding.occurrence_rate
    : null;
  if (runCount === null || occurrenceRate === null) {
    return null;
  }
  return Math.max(0, Math.min(runCount, Math.round(occurrenceRate * runCount)));
}

export function findingOccurrenceLabel(finding) {
  const detectionCount = typeof finding?.occurrence_count === 'number' && Number.isFinite(finding.occurrence_count)
    ? Math.max(0, Math.round(finding.occurrence_count))
    : null;
  const runCount = typeof finding?.run_count === 'number' && finding.run_count > 0 ? finding.run_count : null;
  const supportingRunCount = derivedSupportingRunCount(finding);

  if (runCount !== null && supportingRunCount !== null) {
    if (detectionCount !== null && detectionCount > supportingRunCount) {
      return `Seen in ${supportingRunCount} of ${runCount} runs (detected ${pluralizeCount(detectionCount, 'time')})`;
    }
    return `Seen ${supportingRunCount} of ${runCount} runs`;
  }
  if (detectionCount !== null) {
    return `Detected ${pluralizeCount(detectionCount, 'time')}`;
  }
  return 'Detection count unavailable';
}

export function roleCanOperateRuntimeSources(role) {
  return role === 'operator' || role === 'tenant_admin';
}

export function runtimeSourceWindowLabel(sourceConnection) {
  const windowMinutes = sourceConnection?.collection_window_minutes;
  if (typeof windowMinutes !== 'number' || windowMinutes <= 0) {
    return 'recent window';
  }
  if (windowMinutes % 1440 === 0) {
    const days = windowMinutes / 1440;
    return `${days} ${days === 1 ? 'day' : 'days'}`;
  }
  if (windowMinutes % 60 === 0) {
    const hours = windowMinutes / 60;
    return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  }
  return `${windowMinutes} min`;
}

export function componentOptionLabel(component) {
  if (!component) {
    return '';
  }
  return component.display_name || component.name;
}

export function sourceTypeLabel(sourceType) {
  switch (sourceType) {
    case 'smart_trace':
    case 'auto':
      return 'Smart trace analysis';
    case 'otel':
      return 'OpenTelemetry (OTEL JSON)';
    case 'jsonl':
      return 'JSONL Trace Export';
    case 'agent_session_jsonl':
      return 'Agent Session JSONL';
    case 'langsmith':
      return 'LangSmith JSON';
    case 'langfuse':
      return 'Langfuse JSON';
    case 'runtime_json':
      return 'Canonical Runtime JSON';
    case 'aws_xray':
      return 'AWS X-Ray JSON';
    default:
      return sourceType || 'Unknown source type';
  }
}

export function workflowOptionLabel(workflow) {
  return workflow?.name || '';
}

export function componentMetricValue(metric) {
  if (!metric || typeof metric.value !== 'number') {
    return 'n/a';
  }
  if (metric.unit === 'ratio') {
    return formatPercent(metric.value);
  }
  return formatMetricValue(metric.value, metric.unit);
}

export function findingsHref(systemId, filters = {}) {
  const query = new URLSearchParams();
  if (filters.severity) {
    query.set('severity', filters.severity);
  }
  if (filters.status) {
    query.set('status', filters.status);
  }
  if (filters.prevalence) {
    query.set('prevalence', filters.prevalence);
  }
  if (filters.workflowId) {
    query.set('workflow_id', filters.workflowId);
  }
  if (filters.componentId) {
    query.set('component_id', filters.componentId);
  }
  const queryString = query.toString();
  return `#/systems/${systemId}/findings${queryString ? `?${queryString}` : ''}`;
}

export function truncateLabel(value, maxLength = 22) {
  if (!value) {
    return '';
  }
  return value.length > maxLength ? `${value.slice(0, maxLength - 1)}…` : value;
}

export function activeFindingFilterChips(route, workflows = [], components = []) {
  if (route.name !== 'findings-list') {
    return [];
  }
  const workflowNameById = new Map(workflows.map((workflow) => [workflow.id, workflowOptionLabel(workflow)]));
  const componentNameById = new Map(components.map((component) => [component.id, componentOptionLabel(component)]));
  const chips = [];
  if (route.severity) {
    chips.push(`Severity: ${route.severity}`);
  }
  if (route.status) {
    chips.push(`Status: ${findingStatusLabel(route.status)}`);
  }
  if (route.prevalence) {
    chips.push(`Prevalence: ${prevalenceLabel(route.prevalence)}`);
  }
  if (route.workflowId) {
    chips.push(`Workflow: ${workflowNameById.get(route.workflowId) || truncateLabel(route.workflowId, 18)}`);
  }
  if (route.componentId) {
    chips.push(`Component: ${componentNameById.get(route.componentId) || truncateLabel(route.componentId, 18)}`);
  }
  return chips;
}

export function recommendationTypeLabel(type) {
  switch (type) {
    case 'config_change':
      return 'Config change';
    case 'prompt_change':
      return 'Prompt change';
    case 'workflow_change':
    default:
      return 'Workflow change';
  }
}

export function qualityReasonLabel(reason) {
  switch (reason) {
    case 'too_few_components':
      return 'Too few stable components were inferred from the runtime data.';
    case 'too_few_relationships':
      return 'Too few stable relationships were inferred from the runtime data.';
    case 'too_many_components':
      return 'The inferred graph is too broad to display reliably yet.';
    case 'too_many_relationships':
      return 'The inferred graph is too dense to display reliably yet.';
    case 'too_many_step_name_components':
      return 'Too many inferred components came from step names instead of stronger identities.';
    case 'too_few_high_confidence_components':
    case 'too_few_confident_components':
      return 'Too few inferred components have strong enough evidence to trust the graph.';
    case 'step_name_only_graph':
      return 'The graph is based only on repeated step names, which is too noisy to render.';
    case 'no_components':
      return 'No components are available yet.';
    default:
      return 'Not enough reliable component structure is available yet.';
  }
}

export function architectureDisplayMode(architecture) {
  if (architecture?.quality?.display_mode) {
    return architecture.quality.display_mode;
  }
  if (architecture?.quality?.displayable === false) {
    return 'hidden';
  }
  return 'full';
}

export function componentConfidenceBand(component) {
  return component?.metadata?.inference_confidence_band || 'neutral';
}

export function relationshipConfidenceBand(relationship) {
  const confidence = typeof relationship?.confidence === 'number' ? relationship.confidence : Number(relationship?.confidence);
  if (Number.isNaN(confidence)) {
    return 'medium';
  }
  if (confidence >= 0.75) {
    return 'high';
  }
  if (confidence >= 0.5) {
    return 'medium';
  }
  return 'low';
}

export function onboardingStatusLabel(status) {
  switch (status) {
    case 'registered':
      return 'Ingestion registered';
    case 'normalising':
      return 'Normalising trace';
    case 'analysis_pending':
      return 'Analysis queued';
    case 'analysis_running':
      return 'Analysis running';
    case 'completed':
      return 'Analysis completed';
    case 'failed':
      return 'Onboarding failed';
    default:
      return 'Onboarding in progress';
  }
}

export function onboardingStatusHint(status, stalled) {
  if (!stalled) {
    return null;
  }
  if (status === 'normalising' || status === 'analysis_pending' || status === 'analysis_running') {
    return 'Analysis is still processing. Refresh again in a moment if this status does not change.';
  }
  return null;
}

export function navLinkClass(active) {
  return active ? 'nav-link nav-link-active' : 'nav-link';
}

export function scopedCacheKey(namespace, key) {
  return `${namespace}:${key}`;
}

export function readCachedValue(cacheRef, namespace, key) {
  const entry = cacheRef.current.get(scopedCacheKey(namespace, key));
  return entry?.status === 'resolved' ? entry.value : null;
}

export function loadCachedValue(cacheRef, namespace, key, loader) {
  const cacheKey = scopedCacheKey(namespace, key);
  const existingEntry = cacheRef.current.get(cacheKey);
  if (existingEntry?.status === 'resolved') {
    return Promise.resolve(existingEntry.value);
  }
  if (existingEntry?.promise) {
    return existingEntry.promise;
  }

  const promise = Promise.resolve()
    .then(loader)
    .then((value) => {
      cacheRef.current.set(cacheKey, { status: 'resolved', value });
      return value;
    })
    .catch((requestError) => {
      const latestEntry = cacheRef.current.get(cacheKey);
      if (latestEntry?.promise === promise) {
        cacheRef.current.delete(cacheKey);
      }
      throw requestError;
    });

  cacheRef.current.set(cacheKey, { status: 'pending', promise });
  return promise;
}
